import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bike, Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import { SubmitButton } from "@/components/form/SubmitButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/features/auth/AuthContext";
import { riderApi } from "@/features/delivery/api";
import { formatApiError } from "@/features/auth/formatError";
import { ROUTES } from "@/constants/routes";

export default function RiderLogin() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSubmitting(true);
        try {
            await login({ email: email.trim(), password, remember: true, portal: "rider" });
            try {
                await riderApi.me();
            } catch {
                toast.error("This account isn't a rider. Please use the correct login portal.");
                return;
            }
            navigate(ROUTES.riderDashboard, { replace: true });
        } catch (err) {
            setError(formatApiError(err, "Sign-in failed"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="rider-login-page" className="space-y-6">
            <div className="flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-brand text-white">
                    <Bike className="h-5 w-5" />
                </span>
                <div>
                    <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">
                        Rider sign-in
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        Sign in to start accepting deliveries.
                    </p>
                </div>
            </div>

            <form className="space-y-4" onSubmit={onSubmit}>
                <div className="space-y-1.5">
                    <Label htmlFor="r-email">Email</Label>
                    <div className="relative">
                        <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            id="r-email"
                            type="email"
                            className="pl-9"
                            data-testid="rider-login-email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="email"
                            required
                        />
                    </div>
                </div>
                <div className="space-y-1.5">
                    <Label htmlFor="r-pw">Password</Label>
                    <div className="relative">
                        <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            id="r-pw"
                            type="password"
                            className="pl-9"
                            data-testid="rider-login-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="current-password"
                            required
                        />
                    </div>
                </div>
                {error ? (
                    <p role="alert" data-testid="rider-login-error" className="text-sm font-medium text-error">
                        {error}
                    </p>
                ) : null}
                <SubmitButton
                    type="submit"
                    loading={submitting}
                    data-testid="rider-login-submit"
                    className="bg-brand text-white hover:bg-brand-600"
                >
                    Sign in
                </SubmitButton>
            </form>
        </div>
    );
}
